import React, { CSSProperties, Fragment } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import {
  CurrencyConversionRateWithDate,
  PriceAmount,
  PriceAmountWithConversions,
  PriceAmountWithDetails,
  PriceAmountWithDetailsWithConversions,
} from '@/api';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import IconInfo from '@/icons/Info.svg';
import { createBEMClasses } from '@/utils/classname';
import { formatDate } from '@/utils/date';
import { formatCurrency } from '@/utils/number';
import { getConversionPrices } from '@/utils/price';

import { PriceDetailsRowsOptions, usePriceDetailsRows } from './usePriceDetailsRows';

import './PriceWithDetails.css';

const { element } = createBEMClasses('price-with-details');

export const PriceWithDetailsTooltipWithConversions = ({
  price,
  size = 'medium',
  options,
  className,
  variant,
  style,
  direction = 'row',
  tooltipDirection = 'row',
  gap = '0.5rem',
  marginTooltip,
  showConversionTooltip = true,
}: {
  price:
    | PriceAmountWithDetailsWithConversions
    | PriceAmountWithConversions
    | PriceAmountWithDetails
    | PriceAmount;
  size?: 'x-small' | 'small' | 'medium';
  className?: string;
  variant?: 'primary' | 'secondary' | 'warning';
  options: PriceDetailsRowsOptions;
  style?: CSSProperties;
  direction?: 'row' | 'row-reverse';
  tooltipDirection?: 'row' | 'row-reverse';
  marginTooltip?: React.ReactNode;
  gap?: string;
  showConversionTooltip?: boolean;
}) => {
  return (
    <Stack
      style={{
        flexDirection: direction,
        display: 'inline-flex',
        flexWrap: 'nowrap',
      }}
      gap={gap}
    >
      <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
        {marginTooltip}
        <PriceWithDetailsTooltip
          price={price}
          options={options}
          size={size}
          className={className}
          style={style}
          variant={variant ?? 'primary'}
          noVATApplied={price.noVATApplied}
        />
      </Stack>
      {getConversionPrices(price).map((conversion) => {
        return (
          <Stack
            key={conversion.currency}
            style={{
              ...(!variant &&
                variant !== 'warning' && { color: 'var(--color-text-secondary-black)' }),
              flexDirection: direction,
            }}
            gap={gap}
            alignItems="center"
          >
            <InlineText color="disabled">|</InlineText>
            <Stack
              style={{
                flexDirection: tooltipDirection,
              }}
              alignItems="center"
              gap="0.25rem"
              flexWrap="nowrap"
            >
              {showConversionTooltip && <ConversionTooltip conversionRate={conversion.rate} />}
              <PriceWithDetailsTooltip
                price={conversion.amount}
                options={options}
                size={size}
                className={className}
                style={style}
                variant={variant ?? 'secondary'}
                noVATApplied={conversion.noVATApplied}
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const PriceWithDetailsTooltip = ({
  price,
  size = 'medium',
  options,
  className,
  style,
  variant = 'primary',
  noVATApplied,
}: {
  price: PriceAmountWithDetails | PriceAmount;
  size?: 'x-small' | 'small' | 'medium';
  options: PriceDetailsRowsOptions;
  className?: string;
  style?: CSSProperties;
  variant?: 'primary' | 'secondary' | 'warning';
  noVATApplied?: boolean;
}) => {
  const { _ } = useLingui();

  let sections = usePriceDetailsRows(price, {
    ...options,
    showTaxDetails: true,
  });

  // If there is only one section + total, we remove the total as it's not useful in this tooltip
  if (sections.length === 2) {
    sections = sections.slice(0, -1);
  }

  const inlineTextSize = size === 'x-small' ? '200' : '100';

  return (
    <Tooltip
      variant="light"
      size="medium"
      hasOverlayArrow={false}
      textAlign="start"
      content={
        <table className={element('table')}>
          <tbody>
            {sections.map((sectionRows, sectionIndex) => (
              <Fragment key={sectionIndex}>
                {sections.length > 1 && sectionIndex === sections.length - 1 && (
                  <tr className={element('separator')}>
                    <td colSpan={2} />
                  </tr>
                )}
                {sectionRows.map((row, rowIndex) => {
                  const isSectionTotal = rowIndex === sectionRows.length - 1;
                  const isSeparatedSectionTotal =
                    isSectionTotal && sections.length === 1 && sectionRows.length > 1;
                  const isFinalTotal = isSectionTotal && sectionIndex === sections.length - 1;

                  return (
                    <Fragment key={rowIndex}>
                      {isSeparatedSectionTotal && (
                        <tr className={element('separator')}>
                          <td colSpan={2} />
                        </tr>
                      )}
                      <tr
                        key={rowIndex}
                        className={element('row', {
                          negative: !!row.negativeStyle,
                          'first-row-of-section':
                            (rowIndex === 0 && sectionIndex !== 0) || isSeparatedSectionTotal,
                        })}
                      >
                        <td>
                          <InlineText
                            weight={isSectionTotal || isFinalTotal ? 'medium' : 'regular'}
                            color={isSectionTotal || isFinalTotal ? 'primary' : 'secondary'}
                            size={isFinalTotal ? '200' : '300'}
                          >
                            {typeof row.label === 'string' ? row.label : _(row.label)}
                          </InlineText>
                        </td>
                        <td>
                          <InlineText
                            weight={isSectionTotal || isFinalTotal ? 'medium' : 'regular'}
                            color={isSectionTotal || isFinalTotal ? 'primary' : 'secondary'}
                            size={isFinalTotal ? '200' : '300'}
                            nowrap
                          >
                            {formatCurrency(row.amount, price.currency)}
                          </InlineText>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
            {noVATApplied && !options?.showAmountBeforeTaxes && (
              <tr>
                <td colSpan={2} className={element('warning')}>
                  <InlineText color="warning" size="300">
                    <Trans id="price-with-details.rows.noVATApplied.warning">
                      The cost incl. VAT cannot be displayed, due to the operation being
                      cross-border.
                    </Trans>
                  </InlineText>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      }
    >
      <Button variant="style-less">
        <InlineText
          size={inlineTextSize}
          weight={size === 'x-small' ? 'regular' : 'medium'}
          color={variant}
          className={element('trigger', { size }, className)}
          whiteSpace="nowrap"
          style={style}
        >
          {formatCurrency(
            options.showAmountBeforeTaxes ? price.amountBeforeTaxes : price.amount,
            price.currency
          )}
        </InlineText>
      </Button>
    </Tooltip>
  );
};

export const ConversionTooltip = ({
  conversionRate,
}: {
  conversionRate: CurrencyConversionRateWithDate;
}) => {
  const { from, to, rate, date } = conversionRate;
  const dateFormatted = formatDate(parseISO(date), {
    dateStyle: 'medium',
  });

  return (
    <Tooltip
      content={
        <>
          <Trans id="article.conversion-tooltip.line-1">Conversion rate ({dateFormatted}):</Trans>
          <br />
          <Trans id="article.conversion-tooltip.line-2">
            1 {from} = {rate} {to}
          </Trans>
        </>
      }
    >
      <Button variant="style-less">
        <IconInfo style={{ fontSize: '1rem', color: 'var(--color-text-disabled-black)' }} />
      </Button>
    </Tooltip>
  );
};

export const PendingQuoteBadge = () => (
  <Badge
    size="small"
    color="warning"
    hasBorder
    borderRadius="soft"
    noWrap
    style={{ marginTop: 1, marginBottom: 1 }}
  >
    <Trans id="article.price.pending-quote">Pending quote</Trans>
  </Badge>
);
